// @ts-check
// Variáveis dos textos do bot na tela (contrato de bots, seção 6): os grupos do seletor `{{...}}` (as do sistema, da lista
// `BOT_SYSTEM_VARIABLES` do motor, e as do bot gravadas antes no caminho), a troca pelos valores de exemplo da prévia e
// do "Testar bot", e as respostas sensíveis que a equipe vê como "informado".
import { BOT_SYSTEM_VARIABLES } from '../../../shared/bots/bot-variables.js';

/**
 * @typedef {{ group: string, name: string, token: string }} BotVariableChoice
 *   token: o texto inserido no campo ("{{contact.firstName}}")
 */

/** Variáveis do sistema por grupo, na ordem do seletor (seção 6), montadas da lista do motor. */
export const BOT_SYSTEM_VARIABLE_GROUPS = Object.freeze(BOT_SYSTEM_VARIABLES.reduce((groups, path) => {
  const [group, name] = path.split('.');
  return { ...groups, [group]: Object.freeze([...(groups[group] ?? []), name]) };
}, /** @type {Record<string, ReadonlyArray<string>>} */ ({})));

const VARIABLE_PATTERN = /\{\{\s*([a-zA-Z]+)\.([a-zA-Z0-9_.]+)\s*\}\}/g;

/**
 * Opções do seletor de variáveis: as do sistema e as do bot que existem antes do bloco.
 * @param {string[]} botVariables
 * @returns {BotVariableChoice[]}
 */
export function botVariableChoices(botVariables) {
  /** @type {BotVariableChoice[]} */
  const choices = [];
  for (const [group, names] of Object.entries(BOT_SYSTEM_VARIABLE_GROUPS)) {
    for (const name of names) choices.push({ group, name, token: `{{${group}.${name}}}` });
  }
  for (const name of botVariables) choices.push({ group: 'bot', name, token: `{{bot.${name}}}` });
  return choices;
}

/**
 * Insere o token no texto na posição do cursor (ou no fim).
 * @param {string} text @param {string} token @param {number | null} [position]
 * @returns {{ text: string, cursor: number }}
 */
export function insertVariableToken(text, token, position = null) {
  const at = position === null || position < 0 || position > text.length ? text.length : position;
  const next = `${text.slice(0, at)}${token}${text.slice(at)}`;
  return { text: next, cursor: at + token.length };
}

/**
 * Troca as variáveis pelos valores do escopo; sem valor, sai vazio; sensível sai "informado".
 * @param {string | null | undefined} text
 * @param {{ values: Record<string, Record<string, unknown>>, sensitive?: Set<string>, informedText?: string, keepUnknown?: boolean }} scope
 *   values: grupo para nome e valor (`values.bot.pedido`); sensitive: nomes das variáveis do bot sensíveis; keepUnknown: mostra o token
 *   quando não há valor (a prévia do editor)
 */
export function renderBotTemplate(text, scope) {
  if (typeof text !== 'string' || text === '') return '';
  return text.replace(VARIABLE_PATTERN, (token, group, path) => {
    if (group === 'bot' && scope.sensitive?.has(path)) return scope.informedText ?? '';
    /** @type {any} */
    let value = scope.values?.[group];
    for (const part of String(path).split('.')) value = value && typeof value === 'object' ? value[part] : undefined;
    if (value === undefined || value === null || value === '') return scope.keepUnknown ? token : '';
    return String(value);
  });
}

/** Nomes das variáveis do bot usadas num texto. @param {string | null | undefined} text */
export function botVariablesIn(text) {
  if (typeof text !== 'string') return [];
  return [...new Set([...text.matchAll(VARIABLE_PATTERN)].filter((match) => match[1] === 'bot').map((match) => match[2]))];
}
